import { IFrameGuard } from "@/components/iframe-guard/iframe-guard";
import { MsalProvider } from "@azure/msal-react";
import { ConfigCatProvider, OverrideBehaviour, PollingMode, createFlagOverridesFromMap } from "configcat-react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app";
import { config, isDev } from "./config/configuration";
import { applyFaviconColor } from "./config/favicon";
import { FeatureFlag } from "./config/feature-flags";
import "./config/posthog";
import { msalInstance } from "./config/msal";
import Sentry from "./config/sentry";
import i18n from "./i18n/config";
import "./index.css";
import { userConfigStore } from "./store/user-config-store";

// Synchronize i18n with persisted language from store
const persistedLanguage = userConfigStore.getState().language;
i18n.changeLanguage(persistedLanguage);

applyFaviconColor();

const configCatOptions = isDev
  ? {
      flagOverrides: createFlagOverridesFromMap(
        { [FeatureFlag.IsMaintenance]: false },
        OverrideBehaviour.LocalOverRemote,
      ),
    }
  : undefined;

const domNode: HTMLElement = document.getElementById("root") as HTMLElement;
const root = createRoot(domNode, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <StrictMode>
    <IFrameGuard>
      <MsalProvider instance={msalInstance}>
        <ConfigCatProvider sdkKey={config.configcatKey} pollingMode={PollingMode.AutoPoll} options={configCatOptions}>
          <App />
        </ConfigCatProvider>
      </MsalProvider>
    </IFrameGuard>
  </StrictMode>,
);
